import React, { FC, ReactElement } from 'react';
import { styled } from '../../styles';
import { BpDesignSystem } from '../../types';
import { Keys } from '../../utils/keysUtil';
import { randomString } from '../../utils/randomString';
import { Box } from '../Box/Box';
import { Chip } from '../Chip/Chip';
import { FormHelperText } from '../Form/FormHelperText';
import { FormLabel } from '../Form/FormLabel';
import { IconName } from '../Icon/types';
import { Skeleton } from '../Skeleton';
import { Typography } from '../Typography/Typography';

type CommonProps<VALUE> = BpDesignSystem.Props<{
  options:
    | {
        label: string;
        value: VALUE;
        iconName?: IconName;
        dataTest?: string;
      }[]
    | undefined;
  slotProps?: {
    skeleton?: Partial<ChipsSelectSkeletonProps>;
  };
  label?: string;
  loading?: boolean;
  required?: boolean;
  error?: boolean;
  disabled?: boolean;
  fullWidth?: boolean;
  /*! description is visible under label */
  description?: string;
  /*! helperText is visible under chips */
  helperText?: string;
  /*! errorText is visible under chips and will replace helperText */
  errorText?: string;
  id?: string;
  className?: string;
  size?: 'small' | 'medium';
}>;

interface MultipleProps<VALUE> extends CommonProps<VALUE> {
  multiselect: true;
  value: VALUE[] | undefined;
  onSelect: (value: VALUE[]) => void;
}

interface SingleProps<VALUE> extends CommonProps<VALUE> {
  value: VALUE | undefined;
  onSelect: (value: VALUE | undefined) => void;
}

export type ChipsSelectProps<MULTI extends boolean = false, VALUE = string> = MULTI extends true
  ? MultipleProps<VALUE>
  : SingleProps<VALUE>;

export const ChipsSelect = <VALUE = string, MULTI extends boolean = false>({
  options,
  label,
  error,
  fullWidth,
  required,
  id,
  description,
  disabled,
  helperText,
  errorText,
  loading,
  dataTest = 'chips-select',
  slotProps,
  className,
  size = 'medium',
  sx,
  style,
  ...rest
}: ChipsSelectProps<MULTI, VALUE>): ReactElement | null => {
  const multiProps = rest as unknown as MultipleProps<VALUE>;
  const singleProps = rest as unknown as SingleProps<VALUE>;

  const onSelectHandler = (value: VALUE, selected: boolean) => {
    if (multiProps.multiselect) {
      const newValues = selected
        ? (multiProps.value?.filter((selectedValue) => selectedValue !== value) ?? [])
        : [...(multiProps?.value || []), value];

      multiProps.onSelect(newValues);
    } else {
      singleProps.onSelect(selected ? undefined : value);
    }
  };

  const newId = id ?? randomString();

  const handleKeyPress = (e: React.KeyboardEvent, value: VALUE, selected: boolean) => {
    if (Keys.oneOf(e, 'Enter', ' ')) {
      onSelectHandler(value, selected);
    }
  };

  return (
    <StyledWrapper
      className={className}
      data-test={dataTest}
      sx={sx}
      style={style}
      data-test-selected={Array.isArray(rest.value) ? rest.value.join(',') : rest.value}
    >
      <FormLabel error={error} htmlFor={newId} required={required} style={{ marginBottom: '0' }}>
        {label}
      </FormLabel>
      {!!description && (
        <Typography variant="body" small>
          {description}
        </Typography>
      )}
      {!loading ? (
        <StyledChipsWrapper id={newId}>
          {options?.map(({ value: optionValue, label, iconName, dataTest }) => {
            const selected = checkIfSelectedChip(rest.value, optionValue);
            return (
              <Chip
                aria-selected={selected}
                data-test-selected={selected}
                key={(optionValue as any)?.toString()}
                label={
                  <span data-test="select-chip-text" data-test-selected={selected}>
                    {label}
                  </span>
                }
                disabled={disabled}
                iconName={selected ? 'check' : (iconName ?? undefined)}
                variant={selected ? 'filled' : 'outlined'}
                dataTest={dataTest ?? 'chip-select'}
                size={size}
                onClick={
                  !disabled
                    ? () => {
                        onSelectHandler(optionValue, selected);
                      }
                    : undefined
                }
                onKeyDown={(e) => handleKeyPress(e, optionValue, selected)}
              />
            );
          })}
        </StyledChipsWrapper>
      ) : (
        <ChipsSelectSkeleton {...slotProps?.skeleton} small={size === 'small'} />
      )}
      <FormHelperText helperText={helperText} errorText={errorText} error={error} />
    </StyledWrapper>
  );
};

const StyledChipsWrapper = styled('div')`
  display: flex;
  gap: ${({ theme }) => theme.spacingSize.m};
  flex-wrap: wrap;
`;

const StyledWrapper = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacingSize.m};
`;

const checkIfSelectedChip = <VALUE = string>(selectedValues: VALUE | VALUE[], optionValue: VALUE) => {
  if (Array.isArray(selectedValues)) return selectedValues.includes(optionValue);
  return selectedValues === optionValue;
};

interface ChipsSelectSkeletonProps {
  /*! default value: [70, 130] */
  randomWidthAsNumber?: [number, number];
  /*! default value: 25 */
  amountOfChips?: number;
}

const ChipsSelectSkeleton: FC<React.PropsWithChildren<ChipsSelectSkeletonProps & { small?: boolean }>> = ({
  randomWidthAsNumber = [70, 130],
  amountOfChips = 3,
  small
}) => {
  return (
    <StyledChipsSkeletons>
      {[...Array(amountOfChips)]
        .map((_, index) => index)
        .map((index) => (
          <Skeleton
            key={index}
            inline
            variant="rounded"
            height={small ? 24 : 32}
            randomWidthAsNumber={randomWidthAsNumber}
          />
        ))}
    </StyledChipsSkeletons>
  );
};

const StyledChipsSkeletons = styled('div')`
  display: flex;
  gap: ${({ theme }) => theme.spacingSize.m};
  flex-wrap: wrap;
  > * {
    // Simulate chips
    &.MuiSkeleton-root {
      border-radius: ${({ theme }) => theme.borderRadius.l};
      transform: none;
    }
  }
`;
